import { t } from '@/features/i18n/utils';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { BidBreadcrumbs } from '@/features/bids/components/bid-breadcrumbs';
import { createFileRoute } from '@tanstack/react-router';
import z from 'zod';
import { useRef, lazy, Suspense, useState } from 'react';
import { Breadcrumb, BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderChatButton, HeaderBackButton } from '@/ui/components/header';
import { CommentButton } from '@/features/comments/components/comment-button';
import {
  CommentPanelContent,
  CommentPanelFollowToggle,
  CommentPanelHeader,
  CommentPanelInput,
  CommentPanelTitle,
  CommentPanelUsers,
  CommentPanelXClose,
  ResizableCommentPanel,
} from '@/features/comments/components/comment-panel';
import { NotFound } from '@/ui/components/not-found';
import { bidCvDetailsQuery } from '@/features/bid-cvs/queries';
import { SubjectKind } from '@/subjects';
import { useGetCommentCounts } from '@/features/comments/queries';
import { ResizablePanel, ResizablePanelGroup } from '@/ui/components/resizable';
import { MenuIcon, SidebarRightIcon, PanelRightIcon } from '@hugeicons-pro/core-stroke-rounded';
import { PanelRightIcon as PanelRightIconSolid } from '@hugeicons-pro/core-solid-rounded';
import { Icon } from '@/ui/components/icon';
import { Tabs, TabsList, TabsTab, TabsPanel } from '@/ui/components/tabs';
import { Button } from '@/ui/components/button';
import type { CvPreviewRef } from '@/features/bid-cvs/types';
import { CvAssessmentPanel } from '@/features/bid-cvs/components/cv-assessment-panel';
import { useCvRoleAssignments, useSelectedCvPosition } from '@/features/bid-cvs/helpers';
import { CvSidenavList } from '@/features/bid-cvs/components/cv-sidenav-list';
import { getAllBidCvs } from '@/features/bid-cvs/cv-role-assignments';
import {
  activeIndexAtom,
  activeTypeAtom,
  cvAnalysisMobilenavOpenAtom,
  cvAnalysisSidenavCollapsedAtom,
  queryAtom,
  queryTypeAtom,
  tabAtom,
  type CvAnalysisTab,
} from '@/features/bid-cvs/store';
import { SidenavHeader, SidenavHeading, Sidenav, SidenavList } from '@/ui/components/sidenav';
import ReactMarkdown from 'react-markdown';
import { ScrollArea } from '@/ui/components/scroll-area';
import { Badge } from '@/ui/components/badge';
import { useAtom, useSetAtom } from 'jotai';
import {
  Sheet,
  SheetHeader,
  SheetPanel,
  SheetPopup,
  SheetTitle,
  SheetTrigger,
  SheetXClose,
} from '@/ui/components/sheet';
import { H1 } from '@/ui/components/heading';
import { MainHeader, MainHeaderBackButton } from '@/ui/components/main';

const CvPreview = lazy(() =>
  import('@/features/bid-cvs/components/cv-preview').then((module) => ({
    default: module.CvPreview,
  })),
);

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/_layout/cv-analysis/$positionId',
)({
  loader: ({ context, params }) =>
    context.queryClient.query({ ...bidCvDetailsQuery(params.bidId), staleTime: 'static' }),
  component: RouteComponent,
  validateSearch: z.object({
    statementId: z.string().optional(),
  }),
});

function RouteComponent() {
  const { bidId, workspaceId, projectId, positionId } = Route.useParams();
  const { allCategories } = useCvRoleAssignments();
  const selectedPosition = useSelectedCvPosition();
  const [tab, setTab] = useAtom(tabAtom);
  const setQuery = useSetAtom(queryAtom);
  const setQueryType = useSetAtom(queryTypeAtom);
  const setActiveIndex = useSetAtom(activeIndexAtom);
  const setActiveType = useSetAtom(activeTypeAtom);
  const [sidenavCollapsed, setSidenavCollapsed] = useAtom(cvAnalysisSidenavCollapsedAtom);
  const [mobilenavOpen, setMobilenavOpen] = useAtom(cvAnalysisMobilenavOpenAtom);
  const [commentPanelOpen, setCommentPanelOpen] = useState(false);
  const cvPreviewRef = useRef<CvPreviewRef>(null);

  const { countsMap: commentCounts } = useGetCommentCounts({
    type: DOCUMENT_TYPE.tenderPackage,
    resourceId: workspaceId,
  });
  const counts = commentCounts?.get(`${SubjectKind.BID_CV_POSITION}:${positionId}`);

  const cvCount = getAllBidCvs(allCategories).length;

  if (!selectedPosition) return <NotFound />;

  return (
    <>
      <Header>
        <HeaderBackButton />
        <BidBreadcrumbs>
          <BreadcrumbLink
            to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/cv-analysis"
            params={{ projectId, workspaceId, bidId }}
          >
            {t({ en: 'CV quality check', nb: 'CV-kvalitetssjekk' })}
          </BreadcrumbLink>
          <BreadcrumbPage>{t({ en: 'CV details', nb: 'CV-detaljer' })}</BreadcrumbPage>
        </BidBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <MainHeader className="border-b max-md:hidden">
        <H1>
          <MainHeaderBackButton
            to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/cv-analysis"
            params={{ projectId, workspaceId, bidId }}
          />
          {t({ en: 'CV details', nb: 'CV-detaljer' })}
        </H1>
      </MainHeader>
      <Tabs
        className="flex grow min-h-0 @4xl:flex-row min-w-0 flex-col"
        value={tab}
        onValueChange={(value: CvAnalysisTab) => {
          setTab(value);
          if (value === 'summary') {
            setActiveIndex(undefined);
            setActiveType(undefined);
            setQuery(undefined);
            setQueryType(undefined);
          }
        }}
      >
        <ResizablePanelGroup className="flex grow min-h-0 min-w-0">
          <ResizablePanel className="flex flex-col min-h-0 min-w-0" defaultSize={100} minSize={30}>
            <div className="flex grow @3xl:flex-row flex-col min-w-0 min-h-0">
              <Sidenav
                data-collapsed={sidenavCollapsed ? '' : undefined}
                data-expanded={!sidenavCollapsed ? '' : undefined}
                className="@3xl:border-r border-b @3xl:border-b-0 transition-[width] transform-gpu duration-300 ease-smooth data-collapsed:@3xl:w-0 data-collapsed:@3xl:overflow-hidden data-collapsed:@3xl:border-r-0 data-expanded:lg:w-auto data-expanded:@3xl:w-52 data-expanded:@4xl:w-58"
              >
                <SidenavHeader className="pr-2 whitespace-nowrap border-b-0 @3xl:border-b">
                  <SidenavHeading className="whitespace-nowrap">
                    {t({ en: 'CVs', nb: 'CV-er' })}
                    <Badge kind="icon" variant="secondary" size="xxs" className="ml-2 shrink-0">
                      {cvCount}
                    </Badge>
                  </SidenavHeading>
                  <Button
                    variant="ghost"
                    kind="icon"
                    size="sm"
                    className="ml-auto hidden @3xl:flex"
                    aria-expanded={!sidenavCollapsed}
                    onClick={() => setSidenavCollapsed((prev) => !prev)}
                  >
                    <Icon icon={PanelRightIconSolid} />
                  </Button>
                  <Button
                    variant="ghost"
                    kind="icon"
                    size="sm"
                    className="ml-auto @3xl:hidden"
                    aria-expanded={mobilenavOpen}
                    onClick={() => setMobilenavOpen((prev) => !prev)}
                  >
                    <Icon icon={SidebarRightIcon} />
                  </Button>
                </SidenavHeader>
                <Sheet open={mobilenavOpen} onOpenChange={setMobilenavOpen}>
                  <SheetPopup>
                    <SheetPanel className="p-0!">
                      <SheetHeader>
                        <SheetTitle>
                          {t({ en: 'CVs', nb: 'CV-er' })}
                          <SheetXClose />
                        </SheetTitle>
                      </SheetHeader>
                      <SidenavList scrollable={false} className="flex-col items-start!">
                        <CvSidenavList />
                      </SidenavList>
                    </SheetPanel>
                  </SheetPopup>
                </Sheet>
                <SidenavList className="hidden @3xl:flex @3xl:flex-col">
                  <CvSidenavList />
                </SidenavList>
              </Sidenav>
              <div className="flex flex-col grow min-h-0">
                <TabsList
                  containerClassName="w-full"
                  className="min-h-[calc(var(--spacing)*11-var(--spacing)*0.25)] w-full flex items-center gap-1 px-3"
                >
                  {sidenavCollapsed ? (
                    <Button
                      variant="ghost"
                      kind="icon"
                      size="sm"
                      className="hidden @3xl:flex shrink-0"
                      aria-expanded={!sidenavCollapsed}
                      onClick={() => setSidenavCollapsed((prev) => !prev)}
                    >
                      <Icon icon={PanelRightIcon} />
                    </Button>
                  ) : null}
                  <TabsTab value="cv">{t({ en: 'Original CV', nb: 'Original CV' })}</TabsTab>
                  <TabsTab value="summary">{t({ en: 'Summary', nb: 'Sammendrag' })}</TabsTab>
                  <CommentButton
                    totalCount={counts?.totalCount ?? 0}
                    unreadCount={counts?.unreadCount ?? 0}
                    data-selected={commentPanelOpen ? '' : undefined}
                    className="ml-auto"
                    onClick={() => setCommentPanelOpen(true)}
                  />
                  <Sheet>
                    <SheetTrigger
                      render={
                        <Button
                          variant="ghost"
                          kind="icon"
                          size="sm"
                          className="@4xl:hidden shrink-0"
                          aria-label={t({ en: 'CV quality check', nb: 'CV-kvalitetssjekk' })}
                        >
                          <Icon icon={MenuIcon} />
                        </Button>
                      }
                    />
                    <SheetPopup>
                      <SheetPanel className="p-0! flex flex-col min-h-0">
                        <CvAssessmentPanel cvPreviewRef={cvPreviewRef} />
                      </SheetPanel>
                    </SheetPopup>
                  </Sheet>
                </TabsList>
                <TabsPanel value="cv">
                  <Suspense fallback={null}>
                    <CvPreview ref={cvPreviewRef} documentId={selectedPosition.bidDocId} />
                  </Suspense>
                </TabsPanel>
                <TabsPanel value="summary" render={<ScrollArea viewportClassName="p-4" />}>
                  <div className="flex flex-col">
                    <div>
                      <p className="text-sm text-muted">{selectedPosition.positionName}</p>
                      <p className="text-2xl font-semibold mb-4">
                        {t({ en: 'High level quality check', nb: 'Overordnet kvalitetssjekk' })}
                      </p>
                    </div>
                    {selectedPosition.candidateSummary && (
                      <div className="prose">
                        <ReactMarkdown>{selectedPosition.candidateSummary}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                </TabsPanel>
              </div>
            </div>
          </ResizablePanel>
          <ResizableCommentPanel
            open={commentPanelOpen}
            onOpenChange={setCommentPanelOpen}
            key={positionId}
            className="border-l"
            type={DOCUMENT_TYPE.tenderPackage}
            resourceId={workspaceId}
            subjectKind={SubjectKind.BID_CV_POSITION}
            subjectId={positionId}
            subjectLocation={{ bidId }}
          >
            <CommentPanelHeader>
              <CommentPanelTitle>
                <Breadcrumb>
                  <BreadcrumbPage>
                    <span className="line-clamp-1">
                      {selectedPosition.candidateName || t({ en: 'Anonymous', nb: 'Anonym' })}
                    </span>
                  </BreadcrumbPage>
                  <BreadcrumbPage>
                    <span className="line-clamp-1">{selectedPosition.positionName}</span>
                  </BreadcrumbPage>
                </Breadcrumb>
              </CommentPanelTitle>
              <CommentPanelFollowToggle />
              <CommentPanelUsers />
              <CommentPanelXClose onClick={() => setCommentPanelOpen(false)} />
            </CommentPanelHeader>
            <CommentPanelContent />
            <CommentPanelInput className="p-3 pt-0" />
          </ResizableCommentPanel>
        </ResizablePanelGroup>
        <div
          data-assessment
          className="hidden @4xl:flex flex-col max-w-80 shrink-0 @6xl:max-w-96 border-l min-h-0"
        >
          <CvAssessmentPanel cvPreviewRef={cvPreviewRef} />
        </div>
      </Tabs>
    </>
  );
}
